import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  HomeIcon,
  ChartBarIcon,
  UserGroupIcon,
  ShoppingCartIcon,
  TruckIcon,
  DocumentIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  TagIcon,
  PresentationChartLineIcon,
  ShieldCheckIcon,
  BanknotesIcon,
  RectangleStackIcon,
  BuildingOffice2Icon,
  Cog6ToothIcon,
  GlobeAltIcon,
  ArrowDownTrayIcon,
  UserCircleIcon,
  TableCellsIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { usePermissoes } from '../../hooks/usePermissoes';
import { isPermissaoDisponivel } from '../../types/usuarios';

// Registro ÚNICO de navegação — consumido por Sidebar, breadcrumb do Topbar e
// Command Palette. Rotas e permissões idênticas ao Sidebar pré-Íris.

export interface NavItemDef {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  /** undefined = visível a todo autenticado (salvo admin=true). Array = basta UMA das chaves. */
  permissao?: string | string[];
  /** Exige perfil ADMIN — telas de configuração de sistema. */
  admin?: boolean;
  /** Match exato (sem startsWith) — ex.: /compras/metas vs /compras/metas/dimensao. */
  exact?: boolean;
  /** Fica no registro (breadcrumb + ⌘K) mas NÃO aparece na sidebar — ex.: Meu
   *  Perfil, acessível pelo popover do rodapé (igual ao mock Íris). */
  ocultarSidebar?: boolean;
  /**
   * Id estável da tela pra telemetria. **Literal, nunca derivado do path** —
   * `/catalogoDigital` viraria `catalogoDigital`, que o backend rejeita
   * (só aceita kebab-case) e o beacon perderia a tela em silêncio.
   * Formato: `^[a-z0-9]+(-[a-z0-9]+)*$`. Não renomear depois de coletar dado:
   * o histórico fica órfão.
   */
  screenId: string;
}

export interface NavGroupDef {
  /** null = itens de raiz (sem label de grupo). */
  label: string | null;
  /**
   * Cor categórica da seção (chips de ícone do Sidebar) — hex literal como nos
   * charts (codificação por categoria, não chrome de UI). Tons ~400: legíveis
   * sobre as skins escuras (cinza/azul/preto) e sobre a clara.
   */
  hue: string;
  itens: NavItemDef[];
}

// Categorização espelha o shell do design system Íris (design_handoff): grupos
// Compras · Cadastros · Fiscal · Catálogo · Sistema. Rotas/permissões intactas —
// só o agrupamento mudou. "Produtos" do mock não entra (sem rota no UtilyHub).
export const NAV_GROUPS: NavGroupDef[] = [
  {
    label: null,
    hue: '#818cf8',
    itens: [
      { to: '/', label: 'Início', icon: HomeIcon, screenId: 'home' },
      { to: '/dashboard', label: 'Dashboard', icon: PresentationChartLineIcon, permissao: 'dashboard', screenId: 'dashboard' },
      { to: '/explorador', label: 'Explorador de dados', icon: TableCellsIcon, permissao: 'explorador', screenId: 'explorador' },
      { to: '/pedidos', label: 'Pedidos', icon: ShoppingCartIcon, permissao: 'pedidos', screenId: 'pedidos' },
    ],
  },
  {
    label: 'Compras',
    hue: '#34d399',
    itens: [
      { to: '/compras/reposicao', label: 'Gestão de Reposição', icon: PresentationChartLineIcon, permissao: 'compras.reposicao', screenId: 'compras-reposicao' },
      { to: '/compras/analise', label: 'Gerar Sugestão', icon: ChartBarIcon, permissao: 'compras.analise', screenId: 'compras-analise' },
      { to: '/compras/sugestoes', label: 'Sugestões', icon: ClipboardDocumentCheckIcon, permissao: 'compras.sugestoes', screenId: 'compras-sugestoes' },
      { to: '/compras/cotacoes', label: 'Cotações', icon: ClipboardDocumentListIcon, permissao: 'compras.cotacao', screenId: 'compras-cotacoes' },
      { to: '/compras/pedidos', label: 'Pedidos de Compra', icon: ShoppingCartIcon, permissao: 'compras.pedido', screenId: 'compras-pedidos' },
      // exact: /compras/metas/dimensao é sibling com o mesmo prefixo.
      { to: '/compras/metas', label: 'Metas Mensais', icon: BanknotesIcon, permissao: 'compras.metas', exact: true, screenId: 'compras-metas' },
      { to: '/compras/metas/dimensao', label: 'Metas por Dimensão', icon: RectangleStackIcon, permissao: 'compras.metas', screenId: 'compras-metas-dimensao' },
    ],
  },
  {
    label: 'Cadastros',
    hue: '#c084fc',
    itens: [
      { to: '/compras/fornecedores', label: 'Gestão de Fornecedores', icon: BuildingOffice2Icon, permissao: 'compras.fornecedores', screenId: 'compras-fornecedores' },
      { to: '/compras/marcas', label: 'Gestão de Marcas', icon: TagIcon, permissao: 'compras.marcas', screenId: 'compras-marcas' },
      { to: '/agrupamentos', label: 'Agrupamentos', icon: UserGroupIcon, permissao: 'agrupamentos', screenId: 'agrupamentos' },
    ],
  },
  {
    label: 'Fiscal',
    hue: '#fbbf24',
    itens: [
      { to: '/xml-terceiros', label: 'NF-e Terceiros', icon: DocumentIcon, permissao: 'nfe_terceiros', screenId: 'xml-terceiros' },
      { to: '/cte-terceiros', label: 'CT-e Terceiros', icon: TruckIcon, permissao: 'cte_terceiros', screenId: 'cte-terceiros' },
      // screenId "conferencia-lista" (não "conferencia"): existe /conferencia/:id
      // que é o workspace — a distinção lista×detalhe é o motivo de ter registro.
      { to: '/conferencia', label: 'Conferência NF-e', icon: ClipboardDocumentCheckIcon, permissao: 'conferencia_nfe', screenId: 'conferencia-lista' },
      // Chave própria (não conferencia_nfe) — dá caminho de navegação pro supervisor
      // que só trata pendências, sem acesso ao workspace de bipagem da doca.
      { to: '/conferencia/pendencias', label: 'Pendências de recebimento', icon: ExclamationTriangleIcon, permissao: 'conferencia_pendencia', screenId: 'conferencia-pendencias' },
    ],
  },
  {
    label: 'Catálogo',
    hue: '#f472b6',
    itens: [
      { to: '/catalogoDigital', label: 'Catálogo Digital', icon: GlobeAltIcon, permissao: 'catalogo_online', screenId: 'catalogo-digital' },
      { to: '/gerarCatalogo', label: 'Gerar Catálogo', icon: ArrowDownTrayIcon, permissao: 'gerar_catalogo', screenId: 'gerar-catalogo' },
    ],
  },
  {
    label: 'Sistema',
    hue: '#94a3b8',
    itens: [
      // União: quem só tem gerenciar_papeis entra e vê apenas a aba Papéis.
      { to: '/usuarios', label: 'Usuários', icon: UserGroupIcon, permissao: ['gerenciar_usuarios', 'gerenciar_papeis'], screenId: 'usuarios' },
      { to: '/auditoria', label: 'Auditoria', icon: ShieldCheckIcon, permissao: 'auditoria', screenId: 'auditoria' },
      // Organização: ADMIN-only (config global; backend protege com AdminPolicy).
      { to: '/organizacao', label: 'Organização', icon: BuildingOffice2Icon, admin: true, screenId: 'organizacao' },
      // Configurações: livre (Aparência é por usuário; seções globais aparecem só pra ADMIN).
      { to: '/config', label: 'Configurações', icon: Cog6ToothIcon, exact: true, screenId: 'config' },
      // Meu Perfil: só no popover do rodapé + ⌘K (fiel ao mock).
      { to: '/perfil', label: 'Meu Perfil', icon: UserCircleIcon, ocultarSidebar: true, screenId: 'perfil' },
    ],
  },
];

export function itemAtivo(item: NavItemDef, pathname: string): boolean {
  return item.exact
    ? pathname === item.to
    : pathname === item.to || (item.to !== '/' && pathname.startsWith(item.to));
}

/** Grupos de navegação filtrados pelas permissões do usuário (grupos vazios somem). */
export function useNavPermitida(): NavGroupDef[] {
  const { temPermissao, isAdmin } = usePermissoes();
  return useMemo(() => {
    return NAV_GROUPS
      .map((g) => ({
        ...g,
        itens: g.itens.filter((l) => {
          const chaves = l.permissao ? [l.permissao].flat() : [];
          if (chaves.length > 0 && !chaves.some(isPermissaoDisponivel)) return false;
          if (l.admin && !isAdmin) return false;
          return chaves.length === 0 || chaves.some(temPermissao);
        }),
      }))
      .filter((g) => g.itens.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [temPermissao, isAdmin]);
}

/** Label da tela ativa (breadcrumb). Match mais específico vence. */
export function useRotuloRotaAtiva(): string {
  const { pathname } = useLocation();
  return useMemo(() => {
    const todos = NAV_GROUPS.flatMap((g) => g.itens);
    const candidatos = todos.filter((i) => itemAtivo(i, pathname));
    if (candidatos.length === 0) return '';
    // O caminho mais longo é o mais específico (ex.: /compras/metas/dimensao).
    return candidatos.sort((a, b) => b.to.length - a.to.length)[0].label;
  }, [pathname]);
}
